import { useEffect, MutableRefObject } from 'react';
import { FeatureLayer, WebMap } from '../constructors';
import { SimpleMemoryCache } from '../../common/caches/simple-memory-cache';
import { clearEntityCache } from '../helpers/entity-id-to-object-id-maps';
import {
    getFeatureEntityIds,
    isAbortError,
    logMapDebug,
    logMapError,
    logMapWarn,
} from '../helpers/utils';

type CleanupStrategy = 'reset-all' | 'filter-stale';

type UseFeatureLayerCleanupProps<T> = {
    layer?: FeatureLayer;
    webMap?: WebMap;
    cacheKey: string;
    entityIdToObjectIdMapCacheRef: MutableRefObject<SimpleMemoryCache<number>>;
    currentItems?: T[];
    cleanupStrategy?: CleanupStrategy;
    getCurrentEntityIds?: (items: T[]) => number[];
};

export function useFeatureLayerCleanup<T>({
    layer,
    webMap,
    cacheKey,
    entityIdToObjectIdMapCacheRef,
    currentItems = [],
    cleanupStrategy = 'filter-stale',
    getCurrentEntityIds,
}: UseFeatureLayerCleanupProps<T>) {
    useEffect(() => {
        let isStale = false;

        const cleanupStaleFeatures = async () => {
            if (!layer) return;

            try {
                const allFeatures = await layer.queryFeatures();

                if (isStale) {
                    return;
                }

                if (cleanupStrategy === 'reset-all') {
                    const currentEntityIds = getCurrentEntityIds?.(currentItems) || [];
                    const featureEntityIds = getFeatureEntityIds(allFeatures.features);
                    const hasMultipleFeatures = allFeatures.features.length > 1;
                    const hasStaleFeatures =
                        allFeatures.features.length > 0 &&
                        currentEntityIds.length > 0 &&
                        !allFeatures.features.some(
                            (f) =>
                                f.attributes?.entityId &&
                                currentEntityIds.includes(f.attributes.entityId)
                        );
                    const cleanupReason = hasMultipleFeatures
                        ? 'multiple_features'
                        : hasStaleFeatures
                            ? 'stale_single_feature'
                            : undefined;
                    const shouldCleanup = !!cleanupReason;

                    if (shouldCleanup) {
                        logMapWarn({
                            source: 'layer',
                            event: 'cleanup_reset_all',
                            extraData: {
                                layerCacheKey: cacheKey,
                                cleanupReason,
                                totalFeatures: allFeatures.features.length,
                                currentEntityIds,
                                featureEntityIds,
                            },
                        });

                        if (isStale) {
                            return;
                        }

                        await layer.applyEdits({ deleteFeatures: allFeatures.features });

                        if (isStale) {
                            return;
                        }

                        entityIdToObjectIdMapCacheRef.current.reset();
                    }
                } else if (cleanupStrategy === 'filter-stale' && getCurrentEntityIds) {
                    const currentEntityIds = getCurrentEntityIds(currentItems);
                    const staleFeatures = allFeatures.features.filter(
                        (f) =>
                            f.attributes?.entityId &&
                            !currentEntityIds.includes(f.attributes.entityId)
                    );

                    if (staleFeatures.length > 0) {
                        // Debug, not warn: entities leaving the map is routine, and this
                        // logs the cleanup succeeding. At warn it was ~216k events a week.
                        logMapDebug({
                            source: 'layer',
                            event: 'cleanup_stale',
                            extraData: {
                                layerCacheKey: cacheKey,
                                staleCount: staleFeatures.length,
                            },
                        });

                        if (isStale) {
                            return;
                        }

                        await layer.applyEdits({ deleteFeatures: staleFeatures });

                        if (isStale) {
                            return;
                        }

                        staleFeatures.forEach((f) => {
                            if (f.attributes?.entityId) {
                                entityIdToObjectIdMapCacheRef.current.delete(f.attributes.entityId);
                            }
                        });
                    }
                }
            } catch (error) {
                // Esri aborts a pending query when the layer's worker closes — that is,
                // when the view is being destroyed. That's a cancellation, not a failure.
                // Once every MapView started being destroyed on unmount (RND-74018) these
                // became a third of all map log volume while meaning nothing.
                if (isAbortError(error)) {
                    return;
                }
                logMapError({
                    source: 'layer',
                    event: 'cleanup_error',
                    error: error instanceof Error ? error : new Error(String(error)),
                    extraData: {
                        layerCacheKey: cacheKey,
                    },
                });
            }
        };

        cleanupStaleFeatures();

        return () => {
            isStale = true;
        };
    }, [
        layer,
        currentItems,
        cleanupStrategy,
        cacheKey,
        entityIdToObjectIdMapCacheRef,
        getCurrentEntityIds,
    ]);

    useEffect(() => {
        return () => {
            if (layer && webMap) {
                webMap.layers.remove(layer);
                clearEntityCache(cacheKey as any);
            }
        };
    }, [layer, webMap, cacheKey]);
}
